import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import Logo from './Logo';
import Avatar from './ui/Avatar';
import { visibleModules, moduleNav } from '../config/modules';
import styles from './AppSidebar.module.css';

/**
 * The product's navigation.
 *
 * Every destination, grouped, always visible — replacing the hub-of-rooms,
 * which made you choose a room before you could choose a page and turned the
 * landing screen into a menu. The landing screen is now the command centre;
 * navigation lives here where it belongs.
 *
 * The groups are the existing modules and the links are their existing nav
 * arrays, so this stays role-aware for free: a chatter sees four groups, an
 * admin sees nine, and nothing has to be maintained twice.
 *
 * Only the active item is allowed to be loud. Everything else is a muted label
 * — a sidebar where every row competes is a sidebar you stop reading.
 */
function AppSidebar({ onNavigate }) {
  const { user } = useAuth();
  const { unreadCount } = useNotifications();

  const groups = useMemo(() => visibleModules(user)
    .map((m) => ({ id: m.id, name: m.name, items: moduleNav(m, user) }))
    .filter((g) => g.items.length > 0), [user]);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <Logo />
      </div>

      <nav className={styles.nav} aria-label="Main">
        {groups.map((group) => (
          <div key={group.id} className={styles.group}>
            <div className={styles.groupLabel}>{group.name}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const badge = item.id === 'messages' ? unreadCount : 0;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    [styles.item, isActive ? styles.active : ''].filter(Boolean).join(' ')}
                >
                  {Icon && <Icon size={17} strokeWidth={1.75} className={styles.icon} />}
                  <span className={styles.label}>{item.label}</span>
                  {badge > 0 && <span className={styles.badge}>{badge > 9 ? '9+' : badge}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={styles.footer}>
        {/* Avatar takes a user object and a named size — passing `name` and a
            pixel number rendered a "?" at the default size. */}
        <Avatar user={user} size="sm" />
        <div className={styles.who}>
          <div className={styles.whoName}>{user?.firstName} {user?.lastName}</div>
          <div className={styles.whoRole}>{user?.role}</div>
        </div>
      </div>
    </aside>
  );
}

export default AppSidebar;
